import { useEffect, useState } from 'react';
import type { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { EmailResultsProps } from '@/@types/email';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import PostCardVertical from '@/features/posts/post-card-vertical';
import {
  formatNumber,
  formatNumberKeepDecimal,
} from '@/features/tools/helper/formatter';
import { EmailCalculatorResults } from '@/features/ui/components/email-calculator-results';
import { addApolloState, initializeApollo } from '@/graphql';
import { GLOBAL_QUERY } from '@/graphql/globals';
import { cn } from '@/utils/classname';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NumericFormat } from 'react-number-format';

import StackedColumnGraph from '../../../components/graph/stacked-column/page';

const CarLoanCalculatorPage: React.FC<{
  csrfToken: string;
}> = (props) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const [valLocale, setLocale] = useState(locale);
  const [graphData, setGraphData] = useState<any[]>([]);
  const [graphYear, setGraphYear] = useState<string[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);

  const [valCarPrice, setCarPrice] = useState(0.0);
  const [valDownPayment, setDownPayment] = useState(0);
  const [valLoanYear, setLoanYear] = useState(0);
  const [valLoanMonth, setLoanMonth] = useState(0);
  const [valInterestRate, setInterestRate] = useState(0);

  const [valTotalLoanAmount, setValTotalLoanAmount] = useState(0);
  const [valTotalInvestmentWithDividend, setValTotalInvestmentWithDividend] =
    useState(0);
  const [valMonthlyRepaymentAmount, setValMonthlyRepaymentAmount] = useState(0);
  const [valTotalLoanMonth, setValTotalLoanMonth] = useState(0);

  const [showCarPriceError, setShowCarPriceError] = useState(false);
  const [showDownPaymentError, setShowDownPaymentError] = useState(false);
  const [showLoanPeriodError, setShowLoanPeriodError] = useState(false);
  const [showInterestRateError, setShowInterestRateError] = useState(false);
  const [calculated, setCalculated] = useState(false);

  useEffect(() => {
    if (valLocale !== locale) {
      // console.log('Locale changed to:', loc
      setGraphData([]);
      setLocale(locale);
      calculate();
    }
  }, [locale]);

  const resetForm = async () => {
    setCarPrice(0.0);
    setDownPayment(0);
    setLoanYear(0);
    setLoanMonth(0);
    setInterestRate(0);
    setValTotalLoanMonth(0);
    setCalculated(false);
  };

  const calculate = async () => {
    const totalLoanMonth = valLoanYear * 12;
    setValTotalLoanMonth(totalLoanMonth);

    if (valCarPrice <= 0) {
      setShowCarPriceError(true);
    } else {
      setShowCarPriceError(false);
    }

    if (valDownPayment <= 0) {
      setShowDownPaymentError(true);
    } else {
      setShowDownPaymentError(false);
    }

    if (totalLoanMonth <= 0) {
      setShowLoanPeriodError(true);
    } else {
      setShowLoanPeriodError(false);
    }

    if (valInterestRate <= 0) {
      setShowInterestRateError(true);
    } else {
      setShowInterestRateError(false);
    }

    // console.log('valCarPrice', valCarPrice);
    // console.log('valDownPayment', valDownPayment);

    // console.log('valInterestRate', valInterestRate);
    // console.log('valLoanYear', valLoanYear);

    const principleAmount = valCarPrice * (1 - valDownPayment / 100);
    const totalInterestRate = (valInterestRate / 100) * valLoanYear;
    const totalInterest = principleAmount * totalInterestRate;
    const totalLoanAmount = principleAmount + totalInterest;

    const monthlyRepaymentAmount = totalLoanAmount / totalLoanMonth;

    // console.log('upfrontAmount', principleAmount);
    // console.log('totalInterestRate', totalInterestRate);
    // console.log('totalInterest', totalInterest);
    // console.log('totalLoanAmount', totalLoanAmount);
    // console.log('monthlyRepaymentAmount', monthlyRepaymentAmount);

    setValTotalLoanAmount(totalLoanAmount);
    setValMonthlyRepaymentAmount(monthlyRepaymentAmount);

    let currentYear = 0;
    let currentInterestPaid = 0;
    let currentPrinciplePaid = 0;
    let currentOutstandingBalance = totalLoanAmount;
    const yearlyData = [];
    for (let i = 0; i < valLoanYear; i++) {
      currentYear = i + 1;
      currentInterestPaid = currentInterestPaid + totalInterest / valLoanYear;
      currentPrinciplePaid =
        currentPrinciplePaid +
        totalInterest / valLoanYear +
        principleAmount / valLoanYear;
      currentOutstandingBalance = Math.floor(
        currentOutstandingBalance - monthlyRepaymentAmount * 12,
      );

      const currentYearObj = {
        graphYear: currentYear,
        principle: currentPrinciplePaid,
        interest: currentInterestPaid,
        outstandingBalance:
          currentOutstandingBalance < 0 ? 0 : currentOutstandingBalance,
      };

      yearlyData.push(currentYearObj);
    }

    // console.log('yearlyData', yearlyData);

    const yearlyTableMap = Object.values(yearlyData);

    const tableTotalPrinciple = yearlyTableMap.map(
      (item: any) => item.principle,
    );
    const tableTotalInterest = yearlyTableMap.map((item: any) => item.interest);

    const tableOutstandingBalance = yearlyTableMap.map(
      (item: any) => item.outstandingBalance,
    );

    const graphYear = yearlyTableMap.map((item: any) => item.graphYear);

    setGraphYear(graphYear);
    setGraphData([
      {
        name: t('calculatorsPage.carLoan.principle'),
        data: tableTotalPrinciple,
        color: '#CCEDFE',
      },
      {
        name: t('calculatorsPage.carLoan.interest'),
        data: tableTotalInterest,
        color: '#33B8FC',
      },
      {
        name: t('calculatorsPage.carLoan.outstandingBalance'),
        data: tableOutstandingBalance,
        color: '#2D75C5',
      },
    ]);

    setTableData(yearlyTableMap);

    console.log('yearlyTableMap', yearlyTableMap);

    setCalculated(true);
  };

  const getEmailData = (): EmailResultsProps => {
    if (!calculated) {
      return null;
    }

    return {
      resultsImageName: 'car_loan',
      resultsTitle: t('calculatorsPage.email.results'),
      resultsInfo: [
        {
          title: t('calculatorsPage.carLoan.carPrice'),
          value: `RM ${formatNumberKeepDecimal(valCarPrice)}`,
        },
        {
          title: `${t('calculatorsPage.carLoan.downPayment')} (%)`,
          value: `${valDownPayment}`,
        },
        {
          title: t('calculatorsPage.carLoan.loanPeriod'),
          value: `${valLoanYear} ${t('calculatorsPage.email.years')}`,
        },
        {
          title: t('calculatorsPage.carLoan.interestRate'),
          value: `${valInterestRate}`,
        },
      ],
      tableData: {
        headers: [
          [
            { title: t('calculatorsPage.carLoan.year'), rowspan: 2 },
            {
              title: t('calculatorsPage.carLoan.accumulatedYearlyPayment'),
              colspan: 2,
            },
            {
              title: t('calculatorsPage.carLoan.outstandingBalance'),
              rowspan: 2,
            },
          ],
          [
            { title: t('calculatorsPage.carLoan.principle') },
            { title: t('calculatorsPage.carLoan.interest') },
          ],
        ],
        data: tableData.map((data) => ({
          Year: data.graphYear,
          Principal: `RM ${formatNumberKeepDecimal(data.principle)}`,
          Interest: `RM ${formatNumberKeepDecimal(data.interest)}`,
          'Outstanding Balance': `RM ${formatNumberKeepDecimal(
            data.outstandingBalance,
          )}`,
        })),
      },
      graphData: graphData.map((item) => ({
        name: item.name,
        data: item.data,
      })),
      graphYears: graphYear.map(
        (year) => `${t('calculatorsPage.common.year')} ${year}`,
      ),
      graphColors: ['#98ffff', '#2aaae2', '#19237e'],
    };
  };

  return (
    <div className="bg-neutral-100 pb-32 pt-2 md:py-32">
      <div className="mx-auto max-w-6xl space-y-2">
        <div className="grid grid-flow-col gap-x-2 overflow-x-auto md:grid-cols-5">
          <Link href="/calculators/compound-periodic">
            <div className="flex h-full min-w-[185px] cursor-pointer flex-col justify-center rounded bg-white p-3 text-center text-sm font-bold text-[#80939B] hover:border-b-2 hover:border-[#193CEB] hover:text-[#193CEB]">
              <div>{t('calculatorsPage.periodic.title1')}</div>
              <div>{t('calculatorsPage.periodic.title2')}</div>
            </div>
          </Link>
          <Link href="/calculators/lump-sum">
            <div className="flex h-full min-w-[185px] cursor-pointer flex-col justify-center rounded bg-white p-3 text-center text-sm font-bold text-[#80939B] hover:border-b-2 hover:border-[#193CEB] hover:text-[#193CEB]">
              <div>{t('calculatorsPage.lumpsum.title1')}</div>
              <div>{t('calculatorsPage.lumpsum.title2')}</div>
            </div>
          </Link>
          <Link href="/calculators/start-early">
            <div className="flex h-full min-w-[185px] cursor-pointer flex-col justify-center rounded bg-white p-3 text-center text-sm font-bold text-[#80939B] hover:border-b-2 hover:border-[#193CEB] hover:text-[#193CEB]">
              <div>{t('calculatorsPage.startEarly.title1')}</div>
              <div>{t('calculatorsPage.startEarly.title2')}</div>
            </div>
          </Link>
          <Link href="/calculators/car-loan">
            <div className="flex h-full min-w-[185px] cursor-pointer flex-col justify-center rounded border-b-2 border-[#193CEB] bg-white p-3 text-center text-sm font-bold text-[#193CEB]">
              <div>{t('calculatorsPage.carLoan.title1')}</div>
              <div>{t('calculatorsPage.carLoan.title2')}</div>
            </div>
          </Link>
          <Link href="/calculators/saving">
            <div className="flex h-full min-w-[185px] cursor-pointer flex-col justify-center rounded bg-white p-3 text-center text-sm font-bold text-[#80939B] hover:border-b-2 hover:border-[#193CEB] hover:text-[#193CEB]">
              <div>{t('calculatorsPage.saving.title1')}</div>
              <div>{t('calculatorsPage.saving.title2')}</div>
            </div>
          </Link>
          {/* <Link href="/calculators/education">
            <div className="flex h-full min-w-[185px] cursor-pointer flex-col justify-center rounded bg-white p-3 text-center text-sm font-bold text-[#80939B] hover:border-b-2 hover:border-[#0085C9] hover:text-[#0085C9]">
              <div>{t('calculatorsPage.education.title1')}</div>
              <div>{t('calculatorsPage.education.title2')}</div>
            </div>
          </Link> */}
        </div>
        <div className="flex flex-col items-center space-y-[16px] bg-white px-[40px] py-[20px] md:flex-row md:space-x-[24px]">
          <div className="w-full font-serif text-xl font-black text-[#193CEB] md:w-5/12 lg:text-3xl">
            <div>
              {t('calculatorsPage.carLoan.title1')}{' '}
              {t('calculatorsPage.carLoan.title2')}
            </div>
            {/* <div>{t('calculatorsPage.carLoan.title2')}</div> */}
          </div>
          <div className="w-full flex-col bg-[#E6F6FF] px-[20px] py-[12px] md:w-7/12">
            <div className="font-sm">
              {t('calculatorsPage.carLoan.description')}
            </div>
          </div>
        </div>
        <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0">
          <div className="w-full flex-col bg-white p-[40px] md:w-6/12">
            <div className="w-full">
              <div className="text-brandAlt-950 lg:text-xl">
                {t('calculatorsPage.carLoan.carPrice')}
              </div>
              <div className="py-2.5">
                <div className="pointer-events-none absolute flex items-center pl-[16px]">
                  <span className="py-[16px] font-semibold text-slate-900 sm:text-sm lg:text-xl">
                    RM
                  </span>
                </div>
                <div>
                  <NumericFormat
                    className={
                      showCarPriceError
                        ? 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-red-600 py-[12px] pl-14 pr-[16px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-inset sm:leading-6  md:w-1/2'
                        : 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-gray-200 py-[12px] pl-14 pr-[16px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-inset sm:leading-6  md:w-1/2'
                    }
                    thousandsGroupStyle="thousand"
                    thousandSeparator=","
                    decimalScale={2}
                    maxLength={14}
                    fixedDecimalScale
                    placeholder="0.00"
                    value={valCarPrice === 0.0 ? '' : valCarPrice}
                    onChange={(e) =>
                      setCarPrice(Number(e.target.value.replace(/,/g, '')))
                    }
                  />
                  {showCarPriceError && (
                    <div className="mt-2 text-red-600">
                      {t('calculatorsPage.common.errorState')}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-8 w-full">
              <div className="text-brandAlt-950 lg:text-xl">
                {t('calculatorsPage.carLoan.downPayment')}
              </div>
              <div>
                <div className="flex space-x-[24px]">
                  <div className="w-full py-2.5 md:w-1/3">
                    <div className="w-full">
                      <NumericFormat
                        className={
                          showDownPaymentError
                            ? 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-red-600 pr-[12px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2  focus:ring-inset sm:leading-6'
                            : 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-gray-200 pr-[12px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2  focus:ring-inset sm:leading-6'
                        }
                        placeholder="10%"
                        maxLength={3}
                        suffix="%"
                        value={valDownPayment === 0 ? '' : valDownPayment}
                        onChange={(e) => {
                          setDownPayment(
                            Number(e.target.value.replace('%', '')),
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
                {showDownPaymentError && (
                  <div className="text-red-600">
                    {t('calculatorsPage.common.errorState')}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 w-full">
              <div className="text-brandAlt-950 lg:text-xl">
                {t('calculatorsPage.carLoan.loanPeriod')}
              </div>
              <div className="py-2.5">
                <div className="flex space-x-[24px]">
                  <div className="w-full md:w-1/3 ">
                    <div className="pointer-events-none absolute flex items-center pl-[16px]">
                      <span className="py-[16px] font-semibold text-slate-900 sm:text-sm lg:text-xl">
                        {t('calculatorsPage.common.years')}
                      </span>
                    </div>
                    <div>
                      <NumericFormat
                        className={
                          showLoanPeriodError
                            ? 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-red-600 py-[12px] pl-20 pr-[16px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-inset sm:leading-6'
                            : 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-gray-200 py-[12px] pl-20 pr-[16px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-inset sm:leading-6'
                        }
                        placeholder="0"
                        maxLength={1}
                        value={valLoanYear === 0 ? '' : valLoanYear}
                        onChange={(e) => setLoanYear(Number(e.target.value))}
                      />
                    </div>
                  </div>
                  {/* <div className="w-1/3 ">
                    <div className="pointer-events-none absolute flex items-center pl-[16px]">
                      <span className="py-[16px] font-semibold text-slate-900 sm:text-sm lg:text-xl">
                        Months
                      </span>
                    </div>
                    <div>
                      <NumericFormat
                        className={
                          showLoanPeriodError
                            ? 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-red-600 py-[12px] pl-24 pr-[16px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2  focus:ring-inset sm:leading-6'
                            : 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-gray-200 py-[12px] pl-24 pr-[16px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2  focus:ring-inset sm:leading-6'
                        }
                        placeholder="0"
                        maxLength={3}
                        value={valLoanMonth === 0 ? '' : valLoanMonth}
                        onChange={(e) => setLoanMonth(Number(e.target.value))}
                      />
                    </div>
                  </div> */}
                </div>
                {showLoanPeriodError && (
                  <div className="mt-2 text-red-600">
                    {t('calculatorsPage.common.errorState')}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 w-full">
              <div className="text-brandAlt-950 lg:text-xl">
                {t('calculatorsPage.carLoan.interestRate')}
              </div>
              <div>
                <div className="flex space-x-[24px]">
                  <div className="w-full py-2.5 md:w-1/3">
                    <div className="w-full">
                      <NumericFormat
                        className={
                          showInterestRateError
                            ? 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-red-600 pr-[12px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2  focus:ring-inset sm:leading-6'
                            : 'focus:shadow-outline block h-[60px] w-full appearance-none rounded-lg border-gray-200 pr-[12px] text-xl leading-tight text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2  focus:ring-inset sm:leading-6'
                        }
                        placeholder="10%"
                        maxLength={5}
                        suffix="%"
                        value={valInterestRate === 0 ? '' : valInterestRate}
                        onChange={(e) => {
                          setInterestRate(
                            Number(e.target.value.replace('%', '')),
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
                {showInterestRateError && (
                  <div className="text-red-600">
                    {t('calculatorsPage.common.errorState')}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 flex w-full space-x-[30px]">
              <div>
                <Button
                  className="h-[56px] w-[129px] rounded-md px-[24px] py-[12px]"
                  onClick={() => {
                    calculate();
                  }}
                >
                  {t('calculatorsPage.common.calculate')}
                </Button>
              </div>
              <div>
                <Button
                  className="h-[56px] rounded-md px-[24px] py-[12px]"
                  variant="outline"
                  onClick={() => {
                    resetForm();
                  }}
                >
                  {t('calculatorsPage.common.reset')}
                </Button>
              </div>
            </div>
          </div>
          <div className="relative w-full flex-col bg-white p-[40px] md:w-6/12">
            <div className="pb-44">
              {valTotalLoanMonth ? (
                <div>
                  <div>
                    <div className="text-brandAlt-950 w-4/12 font-serif text-xl font-black lg:text-3xl">
                      {t('calculatorsPage.common.result')}
                    </div>
                    <Separator className="my-[20px]" />
                    <div className="text-xl text-[#00346E]">
                      {t('calculatorsPage.carLoan.monthlyRepayment')}
                    </div>
                    <div className="mt-[12px] text-3xl font-black text-[#0085C9]">
                      RM {formatNumberKeepDecimal(valMonthlyRepaymentAmount)}
                    </div>
                    <Separator className="my-[20px] w-1/2 bg-[#33B8FC]" />
                    <div className="text-xl text-[#00346E]">
                      {t('calculatorsPage.carLoan.totalLoanAmount')}
                    </div>
                    <div className="mt-[12px] text-3xl font-black text-[#2CC557]">
                      RM {formatNumberKeepDecimal(valTotalLoanAmount)}
                    </div>
                    <Separator className="my-[20px] w-1/2 bg-[#2CC557]" />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-brandAlt-950 w-4/12 font-serif text-xl font-black lg:text-3xl">
                    {t('calculatorsPage.common.result')}
                  </div>
                  <Separator className="my-[20px]" />
                  <div className="text-xl text-[#00346E]">
                    {t('calculatorsPage.common.keyInToSeeResult')}
                  </div>
                  <div className="mt-[12px] text-3xl font-black text-[#9BB1BB]">
                    RM -
                  </div>
                  <Separator className="my-[20px] w-1/2 bg-[#9BB1BB]" />
                </div>
              )}
            </div>

            <EmailCalculatorResults
              data={getEmailData()}
              csrfToken={props.csrfToken}
            />
          </div>
        </div>
        {valTotalLoanMonth ? (
          <div className="bg-white p-[40px]">
            <StackedColumnGraph
              data={graphData}
              columnLabel={graphYear}
              xLabel={t('calculatorsPage.common.year')}
            />
          </div>
        ) : null}
      </div>
      <div className="mx-auto max-w-6xl">
        {valTotalLoanMonth ? (
          <div>
            <div className="mt-2 overflow-x-auto">
              <div className="bg-white">
                <div className="flex min-w-max space-x-1">
                  <div className="h-[100px] w-[170px] content-center bg-[#E6F6FF] text-center font-bold md:w-3/12">
                    {t('calculatorsPage.carLoan.year')}
                  </div>
                  <div className="h-[100px] w-[340px] space-y-1 md:w-6/12">
                    <div className="h-[50px] w-full content-center bg-[#E6F6FF] text-center font-bold">
                      {t('calculatorsPage.carLoan.accumulatedYearlyPayment')}
                    </div>
                    <div className="flex h-[50px] w-full space-x-1">
                      <div className="w-6/12 content-center bg-[#E6F6FF] text-center font-bold">
                        {t('calculatorsPage.carLoan.principle')}
                      </div>
                      <div className="w-6/12 content-center bg-[#E6F6FF] text-center font-bold">
                        {t('calculatorsPage.carLoan.interest')}
                      </div>
                    </div>
                  </div>
                  <div className="h-[100px] w-[170px] content-center bg-[#E6F6FF] text-center font-bold md:w-3/12">
                    {t('calculatorsPage.carLoan.outstandingBalance')}
                  </div>
                </div>
              </div>
              <div className="space-y-1 bg-neutral-100">
                {tableData.map((tableData, index) => (
                  <div className="flex min-w-max space-x-1" key={index}>
                    <div className="h-[80px] w-[170px] content-center bg-white text-center font-bold md:w-3/12">
                      {tableData.graphYear}
                    </div>
                    <div className="h-[80px] w-[170px] content-center bg-white text-center font-bold md:w-3/12">
                      RM {formatNumberKeepDecimal(tableData.principle)}
                    </div>
                    <div className="h-[80px] w-[170px] content-center bg-white text-center font-bold md:w-3/12">
                      RM {formatNumberKeepDecimal(tableData.interest)}
                    </div>
                    <div className="h-[80px] w-[170px] content-center bg-white text-center font-bold md:w-3/12">
                      RM {formatNumberKeepDecimal(tableData.outstandingBalance)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1 p-3 text-sm font-semibold text-[#0085C9]">
              <div>{t('calculatorsPage.common.disclaimer')}</div>
              <div>{t('calculatorsPage.carLoan.disclaimer1')}</div>
              <div>{t('calculatorsPage.carLoan.disclaimer2')}</div>
              <div>{t('calculatorsPage.carLoan.disclaimer3')}</div>
              <div>{t('calculatorsPage.carLoan.disclaimer4')}</div>
            </div>
          </div>
        ) : null}
        <div className="mt-[60px] px-[16px] md:px-0">
          <div className="text-brandAlt-950 font-serif text-xl font-black lg:text-3xl">
            {t('calculatorsPage.common.whatToDoNext')}
          </div>
          <div className="mt-[32px] grid grid-flow-col gap-x-[16px] overflow-x-auto md:mt-[40px] md:grid-cols-3 md:gap-x-[32px]">
            <Link href="/posts/kuasa-faedah-kompaun-" target="_blank">
              <div className="min-w-[210px] cursor-pointer">
                <div>
                  <img
                    src="/images/calculator/calculator-article0.jpg"
                    alt=""
                    className="aspect-[16/9] h-full w-full object-cover sm:aspect-[2/1] lg:aspect-[3/2]"
                  />
                </div>
                <div className="mt-[32px] px-[11px]">
                  <div className="text-brandAlt-950 text-xl font-extrabold hover:underline">
                    {t('calculatorsPage.externalLinks.link1Title')}
                  </div>
                  <div className="mt-[16px]">
                    {t('calculatorsPage.externalLinks.link1Subtitle')}
                  </div>
                </div>
              </div>
            </Link>
            <Link
              href={t('calculatorsPage.externalLinks.link2Url')}
              target="_blank"
            >
              <div className="min-w-[210px] cursor-pointer">
                <div>
                  <img
                    src={
                      locale === 'ms'
                        ? '/images/calculator/calculator-article1-bm-new.png'
                        : '/images/calculator/calculator-article1-en-new.png'
                    }
                    alt=""
                    className="aspect-[16/9] h-full w-full object-cover sm:aspect-[2/1] lg:aspect-[3/2]"
                  />
                </div>
                <div className="mt-[32px] px-[11px]">
                  <div className="text-brandAlt-950 text-xl font-extrabold hover:underline">
                    {t('calculatorsPage.externalLinks.link2Title')}
                  </div>
                  <div className="mt-[16px]">
                    {t('calculatorsPage.externalLinks.link2Subtitle')}
                  </div>
                </div>
              </div>
            </Link>
            <Link href="https://www.myasnb.com.my" target="_blank">
              <div className="min-w-[210px] cursor-pointer">
                <div>
                  <img
                    src={
                      locale === 'ms'
                        ? '/images/calculator/calculator-article2-bm-new.png'
                        : '/images/calculator/calculator-article2-en-new.png'
                    }
                    alt=""
                    className="aspect-[16/9] h-full w-full object-cover sm:aspect-[2/1] lg:aspect-[3/2]"
                  />
                </div>
                <div className="mt-[32px] px-[11px]">
                  <div className="text-brandAlt-950 text-xl font-extrabold hover:underline">
                    {t('calculatorsPage.externalLinks.link3Title')}
                  </div>
                  <div className="mt-[16px]">
                    {t('calculatorsPage.externalLinks.link3Subtitle')}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
        <div className="mt-[60px] px-[16px] md:px-0">
          <div className="text-brandAlt-950 w-full font-serif text-xl font-black lg:text-3xl">
            {t('calculatorsPage.common.moreCalculators')}
          </div>
          <div className="mt-[16px] flex flex-col space-y-[12px] md:mt-[24px] md:flex-row md:space-x-10 md:space-y-0">
            <div className=" space-y-[12px]">
              <div>
                <Link
                  href="/calculators/compound-periodic"
                  className="text-brand-500 hover:underline"
                >
                  {t('calculatorsPage.periodic.title1')}{' '}
                  {t('calculatorsPage.periodic.title2')}
                </Link>
              </div>
              <div>
                <Link
                  href="/calculators/lump-sum"
                  className="text-brand-500 hover:underline"
                >
                  {t('calculatorsPage.lumpsum.title1')}{' '}
                  {t('calculatorsPage.lumpsum.title2')}
                </Link>
              </div>
              <div>
                <Link
                  href="/calculators/start-early"
                  className="text-brand-500 hover:underline"
                >
                  {t('calculatorsPage.startEarly.title1')}{' '}
                  {t('calculatorsPage.startEarly.title2')}
                </Link>
              </div>
            </div>
            <div className="space-y-[12px]">
              <div>
                <Link
                  href="/calculators/saving"
                  className="text-brand-500 hover:underline"
                >
                  {t('calculatorsPage.saving.title1')}{' '}
                  {t('calculatorsPage.saving.title2')}
                </Link>
              </div>
              {/* <div>
                <Link
                  href="/calculators/education"
                  className="text-brand-500 hover:underline"
                >
                  {t('calculatorsPage.education.title1')}{' '}
                  {t('calculatorsPage.education.title2')}
                </Link>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  res,
  locale,
}) => {
  const csrfToken = res.getHeader('x-csrf-token') || 'missing';
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query({
    query: GLOBAL_QUERY,
    variables: {
      locale,
      fallbackLocale: 'en',
    },
  });

  return addApolloState(apolloClient, {
    props: {
      csrfToken,
      mainMenu: data?.MainMenu || null,
      topBar: data?.TopBar || null,
      footer: data?.Footer || null,
      categories: data?.Categories?.docs || null,
      postCollections: data?.PostCollections?.docs || null,
      ...(await serverSideTranslations(locale ?? 'en', ['common'])),
    },
  });
};

export default CarLoanCalculatorPage;
